import React, { ChangeEvent, useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableContainer, TablePagination, TableRow } from "@material-ui/core";
import { Toolbar, Typography, Button, Paper, Divider } from "@material-ui/core";
import LinearProgress from "@material-ui/core/LinearProgress";
import SortableTableHead from "src/components/common/SortableTableHead";
import CIRepMetadataModal from "./CIRepMetadataModal"
import { SortingType } from "src/components/common/SortableTableHead";
import { stableSort, getComparator } from "src/components/common/SortableTableHead/tableSort";
import {
    COLUMN_HEADERS,
    EMPTY_RESULTS_MESSAGE,
    INITIAL_MESSAGE, 
    ERROR_MESSAGE_RETRIEVE_CANONICAL_REPRESENTATION_DATA } from "./constant";
import { CIRepMetadata } from "src/model/CIRepMetadata";
import { useStyles } from "src/styles/ResultTableStyles";
import { CanonicalRepresentationOutputItem } from "src/model/CanonicalRepresentationOutputItem";
import {
    downloadCanonicalRepresentationsAsZip,
    downloadCanonicalRepresentationTableDataAsCsv
} from "src/utils/DownloadUtil";
import DownloadDropDownIconButton from "src/components/common/DownloadDropDownIconButton";
import { CanonicalRepresentationRequestInputData } from "src/model/CanonicalRepresentationRequestInputData";


type CIRepResultsTableProps = {
    itemCanonicalRepresentationData: CanonicalRepresentationOutputItem[] | undefined,
    inputData: CanonicalRepresentationRequestInputData,
    displayProgressBar: boolean,
    displayResultsTableErrorMessage: boolean,
    setCanonicalRepresentationToDisplay: (canonicalRepresentationData: CanonicalRepresentationOutputItem) => void,
    setDisplayProgressBar: (display: boolean) => void
}

const CIRepResultsTable: React.FunctionComponent<CIRepResultsTableProps> = (props: CIRepResultsTableProps) => {
    const styles = useStyles();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(8);
    const [selectedItem, setSelectedItem] = useState("");
    const [sortType, setSortType] = useState<SortingType>("desc");
    const [sortBy, setSortBy] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [metadata, setMetadata] = useState<CIRepMetadata>();
    const [displayErrorMessage, setDisplayErrorMessage] = useState(false);

    useEffect(() => {  
        setSelectedItem("")
        setSortType("desc");
        setPage(0);
        setRowsPerPage(8);
        if (props.itemCanonicalRepresentationData) {
            setSortBy("hardwareSource");
        }
    },[props.itemCanonicalRepresentationData]);

    const emptyRows = props.itemCanonicalRepresentationData ?
        rowsPerPage - Math.min(rowsPerPage, props.itemCanonicalRepresentationData.length - page * rowsPerPage) : rowsPerPage;

    const handleSelectRow = (canonicalRepresentationData: CanonicalRepresentationOutputItem) => {
        setSelectedItem(canonicalRepresentationData.uuid);
        props.setCanonicalRepresentationToDisplay(canonicalRepresentationData);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property: string) => {
        const head = COLUMN_HEADERS.find(header => {
            return header.property === property;
        });

        if (head && head.sortable) {
            const isDesc = sortBy === property && sortType === "desc";
            setSortType(isDesc ? "asc" : "desc");
            setSortBy(property);
        }
    }

    const handleCloseModal = () => {
        setModalOpen(false);
        setMetadata(undefined);
    }

    const fetchMetadata = (metadataLocation: string) => {
        fetch(metadataLocation)
        .then(response => response.json())
        .then(metadata =>  { 
            setMetadata(metadata);
            setDisplayErrorMessage(false);
        })
        .catch(error => {
            setMetadata(undefined);
            setDisplayErrorMessage(true);
            console.log('Error while retrieving canonical representation metadata: ', error)
        })
   }

    const handleMetadataDisplay = async (event: React.MouseEvent<any>, metadataLocation: string) => {
        event.stopPropagation();
        setModalOpen(true);
        fetchMetadata(metadataLocation);
    }

    const generateTableBody = () => {
        if (props.itemCanonicalRepresentationData && props.itemCanonicalRepresentationData.length > 0)
            return (
                <>
                    {stableSort(props.itemCanonicalRepresentationData, getComparator(sortType, sortBy))
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((canonicalRepresentationData: CanonicalRepresentationOutputItem) => (
                            <TableRow
                                key={canonicalRepresentationData.uuid}
                                hover={true}
                                selected={canonicalRepresentationData.uuid === selectedItem}
                                onClick={() => handleSelectRow(canonicalRepresentationData)}
                            >
                                <TableCell>{canonicalRepresentationData.hardwareSource}</TableCell>
                                <TableCell>{canonicalRepresentationData.itemId}</TableCell>
                                <TableCell>{canonicalRepresentationData.computeTimestamp}</TableCell>
                                <TableCell>{canonicalRepresentationData.affinityThreshold}</TableCell>
                                <TableCell>{canonicalRepresentationData.captureImageThreshold}</TableCell>
                                <TableCell className={styles.metadataText}>
                                    <Button
                                        className={styles.metadataButton}
                                        onClick={event => handleMetadataDisplay(event, canonicalRepresentationData.metadataLocation)}>
                                        show metadata
                                    </Button>
                             </TableCell>
                            </TableRow>
                        ))
                    }
                    {emptyRows > 0 && (
                        <TableRow style={{height: 33 * emptyRows}}>
                            <TableCell colSpan={COLUMN_HEADERS.length}/>
                        </TableRow>
                    )}
                </>
            );
        else
            return (
                <TableRow style={{height: 33 * emptyRows}}>
                    <TableCell colSpan={COLUMN_HEADERS.length} className={styles.tableMessagePlaceholder}>
                        {getMessageToDisplay()}
                    </TableCell>
                </TableRow>
            )
    }; 

    const getMessageToDisplay = () => {
        if (props.itemCanonicalRepresentationData && props.itemCanonicalRepresentationData.length === 0)
            return (<div className={styles.emptyResultsMessageText}>{EMPTY_RESULTS_MESSAGE}</div>);
        else if (!props.itemCanonicalRepresentationData && props.displayResultsTableErrorMessage)
            return (
                <div className={styles.errorMessageText}>
                    {ERROR_MESSAGE_RETRIEVE_CANONICAL_REPRESENTATION_DATA}
                </div>
            );
        else 
            return (<div className={styles.initialMessageText}>{INITIAL_MESSAGE}</div>);
    };

    const handleDownloadCaptures = async () => {
        props.setDisplayProgressBar(true);
        await downloadCanonicalRepresentationsAsZip(props.itemCanonicalRepresentationData!, props.inputData);
        props.setDisplayProgressBar(false);
    };

    const handleDownloadTableData = () => {
        const downloadColumnHeaders = COLUMN_HEADERS.filter((col) => col.label != "Metadata");
        const sortedTableData = stableSort(props.itemCanonicalRepresentationData!, getComparator(sortType, sortBy));
        const dataRows: any[][] = sortedTableData.map((item: CanonicalRepresentationOutputItem) =>
            downloadColumnHeaders.map(col => {
                const property = (col.property === props.inputData.itemIdType.toLowerCase() ?
                    'itemId' : col.property) as keyof CanonicalRepresentationOutputItem;
                return item[property];
            }));
        downloadCanonicalRepresentationTableDataAsCsv(dataRows, props.inputData, downloadColumnHeaders);
    }

    const downloadOptions = [
        {id: "downloadAsZip", label: "Download All Captures (.zip)", onClick: handleDownloadCaptures},
        {id: "downloadAsCsv", label: "Download Table Data (.csv)", onClick: handleDownloadTableData}
    ];

    return (
        <div>
            <Paper className={styles.paper}>
                <Toolbar variant="dense">
                    <Typography variant="subtitle1" className={styles.title} component="div">Results</Typography>
                    <div className={styles.flow}></div>
                    <DownloadDropDownIconButton
                        disabled={!(props.itemCanonicalRepresentationData && props.itemCanonicalRepresentationData.length > 0)}
                        options={downloadOptions}/>
                </Toolbar>
                {props.displayProgressBar ? <LinearProgress className={styles.progressBar}/> : <div></div>}
                <Divider></Divider>
                <TableContainer>
                    <Table size="small">
                        <SortableTableHead 
                            columnHeads={COLUMN_HEADERS}
                            sortType={sortType}
                            sortBy={sortBy}
                            onRequestSort={handleRequestSort}/>
                        <TableBody>{generateTableBody()}</TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    className={styles.pagination}
                    rowsPerPageOptions={[8, 15, 20]}
                    component="div"
                    count={props.itemCanonicalRepresentationData ? props.itemCanonicalRepresentationData.length : 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
                <CIRepMetadataModal
                    metadata={metadata}
                    open={modalOpen}
                    displayErrorMessage={displayErrorMessage}
                    onCloseModal={handleCloseModal} />       
            </Paper>
        </div>
    );
};

export default CIRepResultsTable;
